import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { AuthenticationService } from '../../core/services/authentication.service';
import { UserService } from '../../core/services/user.service';
import { OauthService } from './index';
import { LoadingService } from './loading.service';
import { OauthProvider } from '../../oauth/providers';
import { User } from '../../core/models/user.model';
import { AccountAttributesEnum } from '../enums';
import { ReconnectSettingModalComponent } from '../../modals/integration-settings/shared/components';
import {  MatDialog,  MatDialogConfig } from '@angular/material/dialog';

export const INTEGRATION_SETTINGS_LOADER: string = 'integration-settings-loader';

@Injectable({
    providedIn: 'root'
})
export class IntegrationSettingsService {
    constructor(
        private readonly authenticationService: AuthenticationService,
        private readonly matDialog: MatDialog,
        private readonly userService: UserService,
        private readonly toastr: ToastrService,
        private readonly oauthService: OauthService,
        private readonly loadingService: LoadingService,
    ) {
    }

    onDynamicsSettings(): Observable<any> {
        return this.openDynamics();
    }

    onHubspotSettings(): Observable<any> {
        const provider: OauthProvider = this.userService.hasAccess(AccountAttributesEnum.HUBSPOT_LITE) ? OauthProvider.HubspotLite : OauthProvider.Hubspot;
        return this.openSetting(provider, AccountAttributesEnum.HUBSPOT, 'hsDisplayName', 'Hubspot');
    }

    onSalesloftSettings(): Observable<any> {
        return this.openSetting(OauthProvider.SalesLoft, AccountAttributesEnum.SALESLOFT, 'slDisplayName','SalesLoft');
    }

    onSalesforceSettings(): Observable<any> {
        return this.openSetting(OauthProvider.Salesforce, AccountAttributesEnum.SALESFORCE, 'sfDisplayName', 'Salesforce');
    }

    onMarketoSettings(): Observable<any> {
        return this.openMarketo();
    }

    onOutreachSettings(): Observable<any> {
        return this.openSetting(OauthProvider.Outreach, AccountAttributesEnum.OUTREACH,'orDisplayName', 'Outreach');
    }

    onZohoSettings(): Observable<any> {
        return this.openSetting(OauthProvider.Zoho, AccountAttributesEnum.ZOHO, 'zhEmail', 'Zoho');
    }

    private openDynamics(): Observable<any> {
        const provider: OauthProvider = OauthProvider.Dynamics;
        return this.loadingService.loadingWrapper(this.userService.getUserData().pipe(
            take(1),
            switchMap((user: User) => {
                if (!this.userService.hasAccess(AccountAttributesEnum.DYNAMICS)) {
                    this.showTrialWarning('Dynamics');
                    return throwError('error');
                }
                if (!this.authenticationService.getDyAuthUrl()) {
                    this.toastr.warning('Your account do not have Dynamics instance URL. Please contact support@salesintel.io.');
                    return throwError('error');
                }
                if (user?.user_meta?.dyEmail) {
                    return this.refreshTokenOfCRM(provider).pipe(catchError((err) => {
                        if (err) {
                            return this.oauthService.openProvider(provider);
                        }
                        return throwError(null);
                    }));
                }
                return this.oauthService.openProvider(provider);
            }),
            catchError(() => {
                return throwError(null);
            })
        ), INTEGRATION_SETTINGS_LOADER);
    }

    private openMarketo(): Observable<any> {
        const provider: OauthProvider = OauthProvider.Marketo;
        return this.loadingService.loadingWrapper(this.authenticationService.updateLoggedUserDataObservable().pipe(
            take(1),
            switchMap((user: User) => {
                if (!this.userService.hasAccess(AccountAttributesEnum.MARKETO)) {
                    this.showTrialWarning('Marketo');
                    return throwError('error');
                }
                return of(provider);
            }),
            catchError(() => {
                return throwError(null);
            }),
        ), INTEGRATION_SETTINGS_LOADER);
    }

    private openSetting(provider: OauthProvider, hasAccess: AccountAttributesEnum, metaKey: string, crmName: string): Observable<any> {
        return this.loadingService.loadingWrapper(this.userService.listenToUserMetaChanges(metaKey).pipe(
            take(1),
            switchMap((metaValue: any) => {
                if (!this.userService.hasAccess(hasAccess)) {
                    this.showTrialWarning(crmName);
                    return throwError('error');
                }
                if (metaValue) {
                    return this.refreshTokenOfCRM(provider).pipe(catchError((err) => {
                        if (err) {
                            return this.oauthService.openProvider(provider);
                        }
                        return throwError(null);
                    }));
                }
                return this.oauthService.openProvider(provider);
            }),
            catchError(() => {
                return throwError(null);
            }),
        ), INTEGRATION_SETTINGS_LOADER);
    }

    private refreshTokenOfCRM(provider: OauthProvider) {
        return this.authenticationService.refreshToken(provider).pipe(
            catchError((e) => {
                if (e.status !== 0 && e.error['errors'].length > 0) {
                    const errorCode = e.error['errors'][0].code;

                    if (errorCode === 'invalid_grant' || errorCode === 'request.invalid' || errorCode === 'BAD_REFRESH_TOKEN' || errorCode === 'BAD_SCOPES') {
                        const modalOptions: MatDialogConfig = {
                            panelClass: 'modal-lg',
                            data: { provider },
                        };
                        return this.matDialog.open(ReconnectSettingModalComponent, modalOptions).afterClosed().pipe(
                            switchMap((res: boolean) => {
                                if (res) {
                                    return throwError(true);
                                }
                                return throwError(null);
                            }),
                        );
                    }
                }

                return of(null);
            }),
        );
    }

    private showTrialWarning(crmName: string): void {
        let message;
        if (this.userService.hasAccess(AccountAttributesEnum.TRIAL)) {
            message = `The free trial plan does not include ` + crmName + ` exports. Check our
                <a class="" href="/upgrade" title="paid plans">Paid plans</a> for upgrade options.`;
        } else {
            message = `Your plan does not include ` + crmName + ` exports.
                <a class="" href="mailto:support@salesintel.io?subject=Upgrade my plan" title="Contact Us" target="_blank">Contact Us</a> for upgrade options.`;
        }
        this.toastr.info(message, 'Warning', { enableHtml: true, timeOut: 10000 });
    }
}
